import styled from 'styled-components'
import { rem } from 'polished'

import LayoutContainer from 'components/Common/LayoutContainer'

import ResponsiveImage from 'components/Common/ResponsiveImage'
import ShareModal from 'components/Common/Modals/ShareModal'
import ShareWithFriendsButton from 'components/Common/ShareWithFriendsButton'
import type HeroProps from './HeroProps'

import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import SignUpBox from '../SignUpBox'

import { mediaQueryUp } from 'components/utils/breakpoint'
import { connect } from 'react-redux'
import TextButton from 'LuxKit/Button/TextButton'
import LineAngleLeftIcon from 'LuxKit/Icons/line/LineAngleLeftIcon'

const ImageContent = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

const ImageWrapper = styled.div`
  width: 100%;
  overflow: hidden;
  max-height: ${rem(480)};
`

const ImageGrid = styled(LayoutContainer)`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
`

const ImageHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${props => props.theme.palette.neutral.default.six};
  background-color: ${props => props.theme.palette.neutral.default.one};
  margin-left: -1rem;
  margin-right: -1rem;
  padding-left: 1rem;
  padding-right: 1rem;

  ${mediaQueryUp.desktop} {
    margin: 0;
    padding: 0;
    margin-top: ${rem(12)};
    background: none;
  }
`

const ImageHeaderCol = styled.div``

const ImageRow = styled.div`
  height: 100%;
  display: flex;
  justify-content: space-between;
`

const ImageLeftText = styled.div<{ isGuest: boolean }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  align-items: center;
  flex-grow: 2;

  ${mediaQueryUp.desktop} {
    align-items: ${({ isGuest }) => isGuest && 'start'};
  }
`

const DesktopSignupCol = styled.div`
  display: none;

  ${mediaQueryUp.desktop} {
    flex-grow: 1;
    flex-shrink: 0;
    flex-basis: 42%;
    display: block;
  }
`

const MobileSignupCol = styled.div`
  display: block;
  margin-top: ${rem(20)};
  padding-left: 1rem;
  padding-right: 1rem;

  ${mediaQueryUp.desktop} {
    display: none;
  }
`

// TEXT TODO: confirm with design
const TextDescription = styled(BodyText)`
  margin-top: ${rem(16)};

  ${mediaQueryUp.largeDesktop} {
    max-width: ${rem(600)};
  }
`

const Wrapper = styled.div`
  position: relative;
`

const Share = styled(ShareModal)`
  display: inline-flex;
  flex-direction: column;
`

interface Props extends HeroProps {
  shareConfig: any
  headlessMode: boolean
  isStoreMode: boolean
  hideSignUpOverlay: boolean
}

function LandingPageHero(props: Props) {
  const shouldReduceBrightness = props.title && props.title.trim().length > 0 || props.subTitle && props.subTitle.trim().length > 0
  const showSignUpOverlay = !(props.hideSignUpOverlay || props.isStoreMode || props.memberId)

  return (<>
    <Wrapper>
      <ImageWrapper>
        <ResponsiveImage
          alt={props.headTitle}
          aria-hidden="true"
          brightness={shouldReduceBrightness ? -35 : undefined}
          gravity="auto"
          mobileAspectRatio="16:10"
          desktopAspectRatio="21:10"
          largeDesktopAspectRatio="3:1"
          id={props.image}
        />
      </ImageWrapper>
      <ImageContent>
        <ImageGrid>
          {!props.headlessMode &&
            <ImageHeaderRow>
              <ImageHeaderCol>
                {props.back && <TextButton
                  kind="tertiary"
                  variant="ghost"
                  to={props.back.link}
                  startIcon={<LineAngleLeftIcon />}
                >
                  {props.back.title}
                </TextButton>}
              </ImageHeaderCol>
              <ImageHeaderCol>
                <Share
                  shareConfig={props.shareConfig}
                >
                  <ShareWithFriendsButton variant="ghost" />
                </Share>
              </ImageHeaderCol>
            </ImageHeaderRow>}
          <ImageRow>
            <ImageLeftText isGuest={!props.memberId}>
              <Heading
                variant="heading1"
                colour="neutral-eight"
                as="h1"
                data-testid="landing-header"
              >
                {props.title}
              </Heading>
              {props.subTitle?.split('\\n').map(st => (
                <TextDescription
                  variant="large"
                  colour="neutral-eight"
                  forwardedAs="h2"
                  key={st}
                >
                  {st}
                </TextDescription>
              ))}
            </ImageLeftText>
            {showSignUpOverlay && (
              <DesktopSignupCol>
                <SignUpBox title={props.title} />
              </DesktopSignupCol>
            )}
          </ImageRow>
        </ImageGrid>
      </ImageContent>
      <div id="end-of-wrapper" />
    </Wrapper>

    {showSignUpOverlay && (
      <MobileSignupCol>
        <SignUpBox title={props.title} prefix="mobile" />
      </MobileSignupCol>
    )}
  </>)
}

function mapStateToProps(state: App.State) {
  return {
    headlessMode: state.system.headlessMode,
    isStoreMode: state.system.storeMode,
  }
}

export default connect(mapStateToProps)(LandingPageHero)
